import { FormInstance } from 'antd';
import { Dayjs } from 'dayjs';
import {
    ArticleInfo,
    ArticleRelations,
    Languages,
    Types,
} from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';

export const enum Statuses {
    DRAFT = 'draft',
    PUBLISH = 'publish',
    ARCHIVE = 'archive',
}

export enum ModalFormMode {
    CREATE = 'create',
    UPDATE = 'update',
    CLOSE = 'close',
}

export type SharedFields = {
    id: string;
    order: number;
    path: string;
    status: Status;
    type: Types;
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
    parent?: string;
};

export type ArticleLinks = {
    linked: ArticleInfo[];
};

export type Entity<LocalizedFields, CommonFields = {}> = SharedFields & {
    [Key in Languages]: LocalizedFields;
} & CommonFields;

export type Status = { current: Statuses; planned?: Statuses; effective?: Date };

export const enum StatusValue {
    DELETED = 'deleted',
    DRAFT = 'draft',
    PLANNED = 'planned',
    PUBLISHED = 'published',
    TO_DELETE = 'to_delete',
}

export type Tag = Entity<{ title: string }, { icon: string }>;

export type Section = Entity<{ title: string }>;
export type Category = Entity<{ title: string }>;

export type Article = Entity<
    { title: string; content: string },
    {
        rating: number;
        scores: number;
        tagId: string;
        linked?: string[];
        views: number;
    }
>;

export type NoContentArticle = {
    [Key in keyof Article]: Key extends Languages
        ? Omit<Article[Key], 'content' | 'views'>
        : Article[Key];
};

export type ArticleWithRelations = Article & ArticleRelations;

export type FullArticle = Article & ArticleRelations & ArticleLinks;

export type KBEntity = Section | Category | Article | NoContentArticle | Tag;

export type UpdateDTO<Entity> = Omit<
    Entity extends NoContentArticle ? Article : Entity,
    'path' | 'type'
>;
export type CreateDTO<Entity> = Omit<UpdateDTO<Entity>, 'id' | 'order'>;

export type WithDraft<Entity extends KBEntity> = (Entity extends NoContentArticle
    ? Omit<Article, 'linked'> & { sectionId: string } & ArticleLinks
    : Entity) & {
    draft: { title: string; content?: string };
};

export interface FormProps<Entity> {
    id: string;
    initialValues: Entity;
    onSubmit: (values: Entity) => void;
    close: () => void;
    form: FormInstance<Entity>;
    tags?: Tag[];
    sections?: Section[];
}

export interface DeleteFormValues {
    id: string;
    effective?: Dayjs;
}

export type ArchiveNode = {
    key: string;
    title: string;
    type: Types;
    updatedAt: string;
    isLeaf?: boolean;
    isDeleted?: boolean;
    children?: ArchiveNode[];
};

export namespace AdminKnowledgeBase {
    export interface Props {
        config: Record<string, any>;
        serviceUrl: Record<string, any>;
        roles: string[];
        privileges: string[];
    }
}
